import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useIsFetching, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AnalyticsIcon } from '../components/icons/AnalyticsIcon'
import { RefreshIcon } from '../components/icons/RefreshIcon'
import { useApiFetch } from '../lib/api'
import { FilterBar } from './mcpAnalytics/FilterBar'
import { InvocationsTab } from './mcpAnalytics/InvocationsTab'
import { SearchActivityTab } from './mcpAnalytics/SearchActivityTab'
import { InvokeActivityTab } from './mcpAnalytics/InvokeActivityTab'
import { useAnalyticsFilters } from './mcpAnalytics/useAnalyticsFilters'
import { MCP_ANALYTICS_BASE, buildFilterParams } from './mcpAnalytics/api'
import { resolvePeriod } from './mcpAnalytics/useAnalyticsFilters'
import type { McpAnalyticsUsersResponse } from '../lib/types'
import { PageHeader } from '../components/PageHeader'

/* ─── MCP Analytics — per-connection page (MS-700 AC5-14, MS-701, MS-702) ───
   Prototype: agent-studio.html:5857-5962 (#mcpobs-editor).

   READ-ONLY SURFACE (MS-700 AC15): no `useDirty()`, no navigation guard, no
   prompt on leave. Nothing here writes. */

export const MCP_ANALYTICS_TAB_IDS = ['tool-invocations', 'search-activity', 'invoke-activity'] as const
export type McpAnalyticsTabId = (typeof MCP_ANALYTICS_TAB_IDS)[number]

export const MCP_ANALYTICS_DEFAULT_TAB: McpAnalyticsTabId = 'tool-invocations'

/**
 * The canonical entry URL for Core's analytics — a tab-NAMING url (MS-700 AC6).
 *
 * Never link to a bare `/mcp-analytics/core`: the prototype's real defect
 * (agent-studio.html:8646-8656) was a second-segment-less hash that failed to
 * match on reload and silently fell back to the overview. Keeping the one
 * canonical path in one exported constant means no call site can reintroduce the
 * bare form.
 */
export const MCP_ANALYTICS_DEFAULT_TAB_PATH = `/mcp-analytics/core/${MCP_ANALYTICS_DEFAULT_TAB}`

/** The connection this page reports on. A route parameter when a second connection
 *  gains analytics; Core is the only live one today (MS-700). */
const CONNECTION_KEY = 'core'

function parseTab(raw: string | null): McpAnalyticsTabId {
  return (MCP_ANALYTICS_TAB_IDS as readonly string[]).includes(raw ?? '')
    ? (raw as McpAnalyticsTabId)
    : MCP_ANALYTICS_DEFAULT_TAB
}

/* `mcpAnalytics.detail.tabs.*` i18n keys predate the TabId rename and were not
 * renamed with it (R-NAV-8's URL-segment change is unrelated to translation keys). */
const TAB_LABEL_KEY: Record<McpAnalyticsTabId, 'invocations' | 'search' | 'invoke'> = {
  'tool-invocations': 'invocations',
  'search-activity': 'search',
  'invoke-activity': 'invoke',
}

export function McpAnalyticsDetail() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const apiFetch = useApiFetch()

  /* ── The active tab is DERIVED from the URL (`/mcp-analytics/:product/:tab`) and
     never mirrored into useState. Same contract as MambuMcpDetail.tsx (which cites
     MS-693 AC13, the identical defect class): exactly ONE writer, `commitTab`
     below. The prototype's AC-6 bug was two independent handlers writing the hash
     for one click, the generic one clobbering the specific one. ── */
  const { product, tab: tabParam } = useParams<{ product: string; tab: string }>()
  const activeTab = parseTab(tabParam ?? null)

  useEffect(() => {
    if (tabParam && !(MCP_ANALYTICS_TAB_IDS as readonly string[]).includes(tabParam)) {
      navigate(`/mcp-analytics/${product}/${MCP_ANALYTICS_DEFAULT_TAB}`, { replace: true })
    }
  }, [tabParam, product, navigate])

  function commitTab(tab: McpAnalyticsTabId) {
    navigate(`/mcp-analytics/${product}/${tab}`)
  }

  const { filters, update, queryKey } = useAnalyticsFilters(CONNECTION_KEY)

  /* ── Reload semantics (MS-700 AC13/AC14) ──
     `reloadToken` is part of every tab's query key, so bumping it re-issues that
     tab's queries rather than serving the cache. All three tab components stay
     mounted (hidden via the `hidden` attribute when inactive), so their React
     Query observers survive tab switches and `placeholderData` can serve the
     previous result while the next fetch is in flight. ── */
  const [reloadToken, setReloadToken] = useState(0)

  const usersQuery = useQuery({
    queryKey: ['mcp-analytics', 'users', CONNECTION_KEY, filters.period, filters.customStart, filters.customEnd, reloadToken],
    placeholderData: (prev) => prev,
    queryFn: () => {
      // Period only: narrowing the picker by the other filters would withdraw
      // options as they are used, so a chosen combination could no longer be
      // undone from the control that made it.
      const { from, to } = resolvePeriod(filters)
      const params = buildFilterParams(
        { ...filters, users: [], channels: [], invocationModes: [] },
        { from, to },
      )
      return apiFetch<McpAnalyticsUsersResponse>(`${MCP_ANALYTICS_BASE}/users?${params.toString()}`)
    },
  })

  /* The control spins and stays inert until the reload has actually completed
     (MS-700 AC13). Driven by react-query's in-flight count for this page rather
     than by a timer: a tab issues two queries (chart and table), so the button
     must not settle after the first, and a fixed timer would either lie about a
     slow query or leave a fast one spinning for no reason. */
  const inFlight = useIsFetching({ queryKey: ['mcp-analytics'] })
  const [refreshRequested, setRefreshRequested] = useState(false)
  const sawFetchRef = useRef(false)
  const isRefreshing = refreshRequested

  useEffect(() => {
    if (!refreshRequested) return
    if (inFlight > 0) {
      sawFetchRef.current = true
      return
    }
    // Only settle once a fetch has actually been observed, so a click that lands
    // before react-query has started does not clear the control immediately.
    if (sawFetchRef.current) {
      sawFetchRef.current = false
      setRefreshRequested(false)
    }
  }, [refreshRequested, inFlight])

  function handleRefresh() {
    if (refreshRequested) return
    setRefreshRequested(true)
    setReloadToken(token => token + 1)
  }

  const tabProps = {
    connectionKey: CONNECTION_KEY,
    filters,
    filtersKey: queryKey,
    reloadToken,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div className="sparks-main">
        <PageHeader
          variant="detail"
          /* MS-700 AC4: the MCP Analytics nav icon, not the MCP product icon. */
          icon={<div className="agent-grid-icon" aria-hidden="true" style={{ color: 'var(--accent-text)' }}><AnalyticsIcon size={22} strokeWidth={2} /></div>}
          title={t('mambuMcp.core.name')}
          caption={t('mambuMcp.core.description')}
          actions={
            /* Refresh sits in the same box as the page-level Save/Discard controls on
               every other Studio page, and reloads the ACTIVE tab only. */
            <button
              type="button"
              className={isRefreshing ? 'pg-act mcpobs-refresh spinning' : 'pg-act mcpobs-refresh'}
              title={isRefreshing ? t('mcpAnalytics.detail.refreshing') : t('mcpAnalytics.detail.refresh')}
              aria-label={t('mcpAnalytics.detail.refresh')}
              aria-busy={isRefreshing}
              disabled={isRefreshing}
              onClick={handleRefresh}
              style={{ color: 'var(--accent)' }}
            >
              <RefreshIcon size={18} strokeWidth={2.2} />
            </button>
          }
        />

        <div className="tabs" role="tablist">
          {MCP_ANALYTICS_TAB_IDS.map(tab => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`mcpobs-tab-${TAB_LABEL_KEY[tab]}`}
              aria-selected={activeTab === tab}
              aria-controls={`mcpobs-panel-${TAB_LABEL_KEY[tab]}`}
              className={activeTab === tab ? 'tab-btn active' : 'tab-btn'}
              onClick={() => commitTab(tab)}
            >
              {t(`mcpAnalytics.detail.tabs.${TAB_LABEL_KEY[tab]}`)}
            </button>
          ))}
        </div>

        {/* The shared filter bar sits below the tabs and OUTSIDE the scroll area, so
            it is unaffected by scrolling and applies to all three tabs. */}
        <FilterBar filters={filters} onChange={update} users={usersQuery.data?.items ?? []} />

        <div className="tab-scroll-area" data-active-tab={activeTab}>
          {/* All three panels stay mounted so React Query observers survive tab
              switches. `hidden` applies display:none and correctly marks the panel
              as invisible to assistive technology without removing it from the DOM. */}
          <div
            className={`tab-panel${activeTab === 'tool-invocations' ? ' active' : ''}`}
            role="tabpanel"
            id="mcpobs-panel-invocations"
            aria-labelledby="mcpobs-tab-invocations"
            hidden={activeTab !== 'tool-invocations'}
          >
            <InvocationsTab {...tabProps} />
          </div>
          <div
            className={`tab-panel${activeTab === 'search-activity' ? ' active' : ''}`}
            role="tabpanel"
            id="mcpobs-panel-search"
            aria-labelledby="mcpobs-tab-search"
            hidden={activeTab !== 'search-activity'}
          >
            <SearchActivityTab {...tabProps} />
          </div>
          <div
            className={`tab-panel${activeTab === 'invoke-activity' ? ' active' : ''}`}
            role="tabpanel"
            id="mcpobs-panel-invoke"
            aria-labelledby="mcpobs-tab-invoke"
            hidden={activeTab !== 'invoke-activity'}
          >
            <InvokeActivityTab {...tabProps} />
          </div>
        </div>
      </div>
    </div>
  )
}
